<script>
import {axios, swal} from '../../mixins';

import * as turf from '@turf/turf'

import Modal from '../Modal.vue';
import MapBox from './MapBox.vue';
import MapBoxSelect from "./MapBoxSelect.vue";
import GeoUpload from './GeoUpload.vue';

export default {
  name: 'Geography',

  components: {Modal, MapBox, MapBoxSelect, GeoUpload},

  mixins: [axios, swal],

  props: {
    source: {
      required: false,
      type: Object,
      default: function () {
        return null;
      },
    },
    uploadUrl: {
      required: true,
      type: String,
    },
    mapboxToken: {
      required: true,
      type: String,
    },
    esriToken: {
      required: true,
      type: String,
    },
    digifarmToken: {
      required: true,
      type: String,
    },
    boundaries: {
      required: false,
      type: Object,
      default: function () {
        return null;
      }
    },
    farmAddress: {
      required: false,
      type: String,
    },
    farmId: {
      required: false,
      type: Number,
    },
    zipCode: {
      required: false,
      type: String,
    },
    // To differentiate selected field ID from surrounding boundaries
    outline: {
      required: false,
      type: Boolean,
      default: false
    },
    // Controlled by tenant setting
    selectEnabled: {
      required: false,
      type: Boolean,
      default: false,
    },
    toolbarHelpItems: {
      required: false,
      type: Array,
      default: function () {
        return [];
      },
    },
  },

  data() {
    return {
      geography: null,
      size: 0,
      center: null,
      geographyString: '',
      showSelect: this.selectEnabled,
      loading: false,
    };
  },

  created() {
    this.processGeography()
  },

  watch: {
    source() {
      if (typeof (this.source) === 'object') {
        this.processGeography()
      }
    },
  },

  methods: {
    /**
     * Set form input values to a received geography
     * @param geography take from input if null
     * @param reloadBoundary reload the map to new boundary
     */
    processGeography(geography = null, reloadBoundary = true) {
      if (!geography) {
        geography = this.source
      }

      if (typeof geography === 'string' || geography instanceof String) {
        geography = JSON.parse(geography);
      }

      if (geography) {
        geography = turf.getGeom(geography)
        if (reloadBoundary) {
          this.geography = geography
        }

        this.size = (turf.area(geography)) * 0.0002471054;
        this.size -= (this.size * 0.00099)
        this.center = turf.getGeom(turf.center(geography)).coordinates;

        this.geographyString = JSON.stringify(geography)
      }
    },

    /**
     * Receive Geography from an input source [MapBox,GeoUpload]
     */
    receiveGeography(event) {
      // Get geography from source
      let geography
      if (event && event.type === 'geo-upload') {
        //get geography from geo upload
        geography = event.geography

        // Process received Geometry
        this.processGeography(geography)

      } else if (event && event.type === 'mapbox') {
        // Get geography from map
        geography = this.$refs['mapComponent'].getGeography().geography

        // Process retrieved geography
        if (geography) {
          this.processGeography(geography, false)
        } else {
          this.clearValues()
          this.$refs['mapSelectComponent'].map.setCenter(event.center);
        }
      } else if (event && event.type === 'mapboxselect') {
        this.$nextTick(function () {
          if (event.geography) {
            geography = event.geography;
            this.$refs['mapComponent'].addGeography(geography)
            this.$refs['mapComponent'].broadcastUpdate();
            this.$refs['mapComponent'].setMapBoundsToFit(geography);
          } else {
            this.$refs['mapComponent'].map.setCenter(event.center);
          }
        })
      }

      this.loading = false;
      this.$nextTick(function () {
        this.resizeMap();
      })

      // Broadcast the updated Geography
      this.broadcastUpdate()
    },

    /**
     * Returns geography as a feature collection. (For external use)
     */
    getValue() {
      return {
        'boundary': this.geographyString,
        'size': this.size,
        'center': this.center
      }
    },

    /**
     * Clear Values
     */
    clearValues() {
      this.geographyString = ''
      this.geography = null;
      this.size = 0;
    },

    /**
     * broadcasts the current data.
     */
    broadcastUpdate() {
      this.$emit('updateGeography', {
        'boundary': this.geographyString,
        'size': this.size,
        'center': this.center
      });
    },

    /**
     * Switch to the editor map
     * loadGeometry = true for loading selections, false for empty editor map
     */
    switchMaps(loadGeometry) {
      this.showSelect = !this.showSelect;
      this.loading = true;

      this.$nextTick(function () {
        if (loadGeometry) {
          let success = this.$refs.mapSelectComponent.generateDrawable();
          if (success) {
            this.resizeMap();
          }
        } else {
          if (this.showSelect) {
            this.$refs.mapComponent.switchMap()
          } else {
            this.$refs.mapSelectComponent.switchMap()
          }
        }
      })
    },

    resizeMap() {
      this.$refs.mapComponent.map.resize();
      this.$refs.mapSelectComponent.map.resize();
    }
  },
};

</script>

<template>
  <div class="container mx-auto text-center">
    <div class="flex flex-col text-left">

      <div>
        <div v-show="this.loading" class="w-full text-center text-3xl font-semibold h-192 my-auto flex flex-col">
          <div class="my-auto">
            Loading...
          </div>
        </div>

        <!-- MAP BOX DRAW -->
        <div v-show="!this.showSelect && !this.loading" class="w-full h-full">
          <MapBox
              class="h-192"
              :source="geography"
              :layer="boundaries"
              :draw="true"
              :token="mapboxToken"
              :esriToken="esriToken"
              :render="false"
              @updateGeography="receiveGeography"
              ref="mapComponent"
              :map-id="'mapDraw'"
              :farmAddress="this.farmAddress"
              :farm-id="this.farmId"
              :zipCode="this.zipCode"
              :outline="this.outline"
              :toolbar-help-items="toolbarHelpItems"
          >
          </MapBox>
        </div>

        <!-- MAP BOX SELECT -->
        <div v-show="this.showSelect" class="w-full h-full">
          <MapBoxSelect
              class="h-192"
              :token="mapboxToken"
              :layers="boundaries"
              :digifarm-token="digifarmToken"
              :esri-token="esriToken"
              @updateGeography="receiveGeography"
              ref="mapSelectComponent"
              :map-id="'mapSelect'"
              :draw="false"
              :farmAddress="this.farmAddress"
              :zipCode="this.zipCode"
          >
          </MapBoxSelect>
        </div>
        <div v-if="this.showSelect" class="grid grid-cols-2">
          <button type="button" id="select-confirm" @click="this.switchMaps(true)" class="button bg-blue text-white hover:bg-blue-dark focus:bg-blue-darker my-2 mr-1 font-bold">Confirm Selection and Edit</button>
          <button type="button" id="select-decline" @click="this.switchMaps(false)" class="button bg-blue text-white hover:bg-blue-dark focus:bg-blue-darker my-2 ml-1 font-bold">Draw New</button>
        </div>
        <div v-if="!this.showSelect && this.selectEnabled">
          <button type="button" id="select-load" @click="this.switchMaps(false)" class="button w-full bg-blue text-white hover:bg-blue-dark focus:bg-blue-darker my-2 font-bold">Load Boundary Selection Tool</button>
        </div>
      </div>

      <div v-show="!this.loading" class="w-full py-3 border-t border-b border-grey-light text-center">
        Or
      </div>

      <!--   INPUT SOURCES   -->
      <div class="flex flex-row py-3 border-b border-grey-light">
        <label class="font-semibold block my-auto" for="boundary">Upload Boundary File <span class="italic">(.shp, .kml, .kmz, .zip)</span></label>

        <!-- FILE UPLOAD -->
        <div class="ml-auto justify-end">
          <GeoUpload :upload-url="uploadUrl" @updateGeography="receiveGeography"></GeoUpload>
        </div>
      </div>
    </div>

    <!-- Acre count display -->
    <label for="size" class="font-semibold block my-2 text-left">Size (Acres)</label>
    <input v-bind="$attrs"
        class="bg-secondary italic rounded border border-grey-lighter w-full py-2 px-3 leading-tight focus:outline-none"
        readonly="readonly"
        aria-label="Size" :value="parseFloat(size).toFixed(1)" name="size" step=".01">

    <!-- Hidden fields -->
    <label for="boundary" class="hidden">Boundary</label>
    <input v-bind="$attrs" id="boundary" readonly="readonly" v-model="geographyString" hidden>

    <label for="center" class="hidden">Centroid</label>
    <input readonly="readonly" id="center" :value="center" name="centroid" hidden>
  </div>
</template>
